import { Briefcase, Target, Trophy, TrendingUp, ChartPie, Award, Flame, Filter as FilterIcon, Users } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { useAdminStats } from '../widgets/admin/hooks/useAdminStats';
import { useLeadVelocityAdapter, useTeamLeaderboardAdapter } from '../widgets/admin/hooks/useAdminCharts';
import { useDeals } from '@/features/deals/hooks/useDeals';
import { formatMoney } from '@/features/deals/constants';
import { DashboardHero } from '../infrastructure/widgets/DashboardHero';
import { OperationalKpiPanel } from '../widgets/operational/OperationalKpiPanel';
import { AreaSparkline } from '../infrastructure/widgets/AreaSparkline';
import { DonutChart } from '../infrastructure/widgets/DonutChart';
import { Leaderboard } from '../infrastructure/widgets/Leaderboard';
import { KpiCard, SectionCard, SimpleList, SimpleListItem } from '../infrastructure/widgets/DashboardKit';

const STAGE_COLORS: Record<string, string> = {
  prospecting: '#5b45d6', qualification: '#7a63de', proposal: '#9a86e6',
  negotiation: '#f79009', closed_won: '#12b76a', closed_lost: '#f04438',
};

export function SalesAdminDashboard() {
  const user = useUser();
  const { data: stats } = useAdminStats();
  const { data: velocity } = useLeadVelocityAdapter();
  const { rows: leaderRows } = useTeamLeaderboardAdapter();
  const { data: dealsData } = useDeals({});

  const o = stats?.overview;
  const deals = dealsData?.deals ?? [];
  const pipelineStats = dealsData?.pipelineStats ?? [];

  const openDeals = deals.filter((d) => d.stage !== 'closed_won' && d.stage !== 'closed_lost');
  const wonDeals = deals.filter((d) => d.stage === 'closed_won');
  const getDealVal = (d: typeof deals[0]) => Number(d.grandTotal ?? d.value ?? 0);
  const pipelineValue = openDeals.reduce((s, d) => s + getDealVal(d), 0);
  const wonValue = wonDeals.reduce((s, d) => s + getDealVal(d), 0);
  const avgDealSize = openDeals.length > 0 ? pipelineValue / openDeals.length : 0;
  const currency = deals[0]?.currency || 'INR';

  const actionableDeals: SimpleListItem[] = [...openDeals]
    .sort((a, b) => getDealVal(b) - getDealVal(a))
    .slice(0, 6)
    .map((d) => ({
      icon: Flame,
      title: d.title,
      meta: `${d.stage.replace('_', ' ')} · ${formatMoney(getDealVal(d), d.currency || 'INR')}`,
      tag: d.stage.replace('_', ' '),
      tagHex: STAGE_COLORS[d.stage] || '#7a63de',
    }));

  return (
    <div className="mx-auto max-w-[1440px]">
      <DashboardHero
        badge="Sales Command · Today"
        badgeIcon={Briefcase}
        title={`Welcome back, ${user?.displayName || 'there'}`}
        subtitle="Your team's pipeline, leads and deal flow at a glance."
        heroStats={[
          { label: 'Pipeline value', value: formatMoney(pipelineValue, currency), sub: `${openDeals.length} open deals`, icon: Target, tone: 'primary' },
          { label: 'Won value', value: formatMoney(wonValue, currency), sub: `${wonDeals.length} won deals`, icon: TrendingUp, tone: 'success' },
          { label: 'Team', value: String(o?.totalEmployees ?? 0), sub: 'active reps', icon: Users, tone: 'info' },
        ]}
      />

      {/* BizPlus-parity operational panel (Phase 4.1): pipeline counts, overdue
          followups, reorder alert and 10-day trends, with a My/Team/All scope. */}
      <div className="mb-5"><OperationalKpiPanel /></div>

      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard label="New Leads" value={String(o?.leadsThisWeek ?? 0)} icon={Target} hex="#5b45d6" />
        <KpiCard label="Converted" value={String(o?.converted ?? 0)} icon={Trophy} hex="#12b76a" />
        <KpiCard label="Open Deals" value={String(openDeals.length)} icon={Briefcase} hex="#7a63de" />
        <KpiCard label="Won Deals" value={String(wonDeals.length)} icon={Award} hex="#f79009" />
        <KpiCard label="Avg Deal Size" value={formatMoney(avgDealSize, currency)} icon={TrendingUp} hex="#0ba5ec" />
        <KpiCard label="Leads Today" value={String(o?.leadsToday ?? 0)} icon={Target} hex="#9a86e6" />
      </div>

      <div className="grid grid-cols-12 gap-4">
        {velocity?.data && (
          <SectionCard icon={TrendingUp} title="Lead Velocity" subtitle="Daily inbound leads, last 14 days" span={8}>
            <AreaSparkline
              values={velocity.data.series[0].data}
              xLabels={velocity.data.categories.filter((_, i) => i % 3 === 0).map((d) => d.slice(5))}
              stat={`${velocity.data.series[0].data.reduce((a, b) => a + b, 0)} leads`}
            />
          </SectionCard>
        )}
        {pipelineStats.length > 0 && (
          <SectionCard icon={ChartPie} title="Pipeline Stages" subtitle="Deals by stage" span={4}>
            <DonutChart
              segments={pipelineStats.map((s) => ({
                label: s.stage.replace('_', ' '),
                value: s.count,
                color: STAGE_COLORS[s.stage] || '#667085',
              }))}
              centerValue={String(deals.length)}
              centerLabel="Deals"
            />
          </SectionCard>
        )}
        {leaderRows.length > 0 && (
          <SectionCard icon={Award} title="Rep Leaderboard" subtitle="Leads generated, all-time" span={6}>
            <Leaderboard rows={leaderRows.slice(0, 5).map((r) => ({ name: r.displayName, value: `${r.totalLeads} leads`, pct: r.barPct }))} />
          </SectionCard>
        )}
        <SectionCard icon={FilterIcon} title="Highest-Value Deals" subtitle="Open pipeline, sorted by value" span={leaderRows.length > 0 ? 6 : 12}>
          <SimpleList items={actionableDeals} />
        </SectionCard>
      </div>
    </div>
  );
}
